import { useEffect, useRef } from "react";
import { useDataChangeEffect, watchSlices } from "../../lib/useDataChangeEffect.js";
import { detectPlatform } from "../../platform.js";
import { useTasksStore } from "../../stores/index.js";
import { expandTasksWindowed, type RuleExpansionCache } from "../calendar-view/build-days.js";
import { reconcile } from "./NotificationScheduler.js";

// Android pre-schedules with the OS, so the window must cover a realistic
// stretch of not-opening-the-app; in-process-timer platforms only need to
// out-run the 5-min tick, and a short window keeps expansion cheap.
const WINDOW_MIN = detectPlatform() === "capacitor" ? 60 * 24 * 14 : 60 * 24;
const TICK_MS = 5 * 60 * 1000;
const DEBOUNCE_MS = 500;

/** Task data is watched imperatively: subscribing Shell to `tasks` re-rendered
 *  the whole app on every edit (see `useDataChangeEffect`). */
const TASK_DATA = [watchSlices(useTasksStore, (s) => [s.tasks, s.rules, s.overrides])];

/**
 * Drives OS reminder notifications: hydrates the task store if needed, then
 * reconciles on boot, whenever tasks/rules/overrides change, and on a 5-min
 * tick. Expands recurring series to virtuals so each upcoming occurrence is
 * armed. Mount once, in the always-present Shell.
 *
 * Boot reconciles immediately; data-change re-runs are DEBOUNCED so the full
 * recurrence expansion stays off the edit critical path (a drag burst re-arms
 * once after it settles, not per edit). Reminders fire on minute buckets, so
 * the sub-second delay is unobservable; the 5-min tick is the safety net.
 *
 * The task data itself is watched IMPERATIVELY (`useDataChangeEffect`) and read
 * via `getState()` when a run fires — selecting it into render would re-render
 * Shell, and with it the whole routed tree, on every edit.
 */
export function useReminderReconciler(): void {
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const cache = useRef<RuleExpansionCache>(new Map());

  const run = () => {
    // Return any snoozed task/occurrence whose wake time has passed before
    // re-arming, so a long-open session both wakes and reschedules reminders.
    const { tasks, rules, overrides, wakeDueSnoozes } = useTasksStore.getState();
    void wakeDueSnoozes();
    const now = new Date();
    // Expand from the START OF TODAY, not the exact instant: intersectsWindow()
    // truncates a task's due to its start-of-day, so passing `now` drops tasks
    // due LATER TODAY (their start-of-day is before now) — they'd never get a
    // reminder. reconcile()/upcomingWithin() re-filter against the precise
    // `now`, so the earlier window start is harmless.
    const from = new Date(now);
    from.setHours(0, 0, 0, 0);
    const to = new Date(now.getTime() + WINDOW_MIN * 60_000);
    const expanded = expandTasksWindowed(tasks, rules, overrides, from, to, cache.current);
    void reconcile(expanded, now, WINDOW_MIN);
  };
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    if (!loaded) void hydrate();
  }, [loaded, hydrate]);

  // Boot run + the 5-min safety tick. Data-change re-runs are debounced below so
  // the full recurrence expansion stays off the edit critical path.
  useEffect(() => {
    if (!loaded) return;
    runRef.current();
    const id = setInterval(() => runRef.current(), TICK_MS);
    return () => clearInterval(id);
  }, [loaded]);

  useDataChangeEffect(TASK_DATA, () => runRef.current(), { debounceMs: DEBOUNCE_MS, enabled: loaded });
}
